"use client";

import { c as _c } from "react/compiler-runtime";
import { addDays } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AvailabilityAppointmentType, AvailabilityResponse } from "@/shared";
import { PersonGender } from "@/shared/models/person";
import ErrorAlert from "@/src/components/error-alert";
import { DetailsStep } from "@/src/components/online-scheduling/details-step";
import { ProgressSteps } from "@/src/components/online-scheduling/progress-steps";
import { LocationSelectionField, ProviderSelectionField } from "@/src/components/online-scheduling/provider-location-fields";
import { ReviewStep } from "@/src/components/online-scheduling/review-step";
import { StepActions } from "@/src/components/online-scheduling/step-actions";
import { SuccessStep } from "@/src/components/online-scheduling/success-step";
import { appointmentTypeForPatient, EMPTY_PATIENT_DETAILS, sanitizeReasonForVisit, SCHEDULING_STEPS, type PatientDetailsDraft, type OnlineSchedulingLocation, type OnlineSchedulingProvider, type SchedulingStepId } from "@/src/components/online-scheduling/types";
import { VisitStep } from "@/src/components/online-scheduling/visit-step";
import { AppointmentSlotPicker } from "@/src/components/scheduling/appointment-slot-picker";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardContent, CardHeader } from "@/src/components/shadcn/ui/card";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/src/components/shadcn/ui/tabs";
import { H4 } from "@/src/components/typography";
import { bookOnlineSchedulingAppointment, fetchOnlineSchedulingAvailability, fetchOnlineSchedulingOptions } from "@/src/lib/scheduling/online-scheduling-client";
import type { OnlineSchedulingBookRequest, OnlineSchedulingBookResponse } from "@/src/lib/scheduling/online-scheduling-schemas";
import { dateFromKey, dateKeyFromDate, normalizeAvailabilitySlot, type NormalizedSlot } from "@/src/lib/scheduling/slot-display";
import { cn } from "@/src/lib/shadcn-utils";
import type { USState } from "@/src/lib/us-states";
import { getCurrentIsoDateInTimezone, isMinorDateOfBirth } from "@/src/lib/utils/date-and-phone-utils";
const INITIAL_WINDOW_DAYS = 30;
const WINDOW_INCREMENT_DAYS = 30;
const MAX_WINDOW_DAYS = 120;
const DETAILS_FORM_ID = "scheduling-details-form";
type FlowStep = SchedulingStepId | "done";
type SchedulingMode = "location" | "provider";
const STEP_TITLE: Record<SchedulingStepId, string> = {
  visit: "Tell us about your visit",
  time: "Pick a time",
  details: "Your details",
  review: "Review & confirm"
};
function availabilitySlotKey(slot: AvailabilityResponse["slots"][number]): string {
  return `${slot.locationId ?? "unknown"}:${slot.appointmentId}`;
}
export function OnlineSchedulingFlow({
  initialLocations,
  initialProviders,
  initialLocationId,
  initialProviderId,
  tenant,
  initialLoadError,
  brandName,
  clinicTimezone
}: {
  initialLocations: OnlineSchedulingLocation[];
  initialProviders: OnlineSchedulingProvider[];
  initialLocationId?: string | null;
  initialProviderId?: string | null;
  tenant?: string | null;
  initialLoadError?: boolean;
  brandName?: string;
  clinicTimezone?: string | null;
}) {
  const findInitialLocation = useCallback((list: OnlineSchedulingLocation[]): OnlineSchedulingLocation | null => {
    const matched = initialLocationId ? list.find(location => location.locationId === initialLocationId || location.ehrId === initialLocationId) : undefined;
    return matched ?? list[0] ?? null;
  }, [initialLocationId]);
  const findInitialProvider = useCallback((list_0: OnlineSchedulingProvider[]): OnlineSchedulingProvider | null => {
    if (!initialProviderId) {
      return null;
    }
    return list_0.find(provider => provider.practitionerId === initialProviderId || provider.ehrId === initialProviderId) ?? null;
  }, [initialProviderId]);
  const [schedulingMode, setSchedulingMode] = useState<SchedulingMode>(initialProviderId ? "provider" : "location");
  const [locations, setLocations] = useState<OnlineSchedulingLocation[]>(initialLocations);
  const [providers, setProviders] = useState<OnlineSchedulingProvider[]>(initialProviders);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(initialLoadError ? "We couldn't load our providers and clinic locations." : null);
  const [selectedLocation, setSelectedLocation] = useState<OnlineSchedulingLocation | null>(() => findInitialLocation(initialLocations));
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(() => findInitialProvider(initialProviders)?.practitionerId);
  const [locationProviderFilterId, setLocationProviderFilterId] = useState<string | null>(null);
  const [step, setStep] = useState<FlowStep>("visit");
  const [isReturningPatient, setIsReturningPatient] = useState(false);
  const [reasonForVisit, setReasonForVisit] = useState("");
  const appointmentType: AvailabilityAppointmentType = appointmentTypeForPatient(isReturningPatient);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(INITIAL_WINDOW_DAYS);
  const [loadedBaseKey, setLoadedBaseKey] = useState<string | null>(null);
  const availabilityRequestId = useRef(0);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilityResponse["slots"][number] | null>(null);
  const [details, setDetails] = useState<PatientDetailsDraft>(EMPTY_PATIENT_DETAILS);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<OnlineSchedulingBookResponse | null>(null);
  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      const response = await fetchOnlineSchedulingOptions(tenant);
      setLocations(response.locations);
      setProviders(response.providers);
      setSelectedLocation(current => {
        const refreshed = current ? response.locations.find(location_0 => location_0.locationId === current.locationId) : undefined;
        if (refreshed) {
          return refreshed;
        }
        return findInitialLocation(response.locations);
      });
      setSelectedProviderId(current_0 => {
        if (current_0 && response.providers.some(provider_0 => provider_0.practitionerId === current_0)) {
          return current_0;
        }
        return findInitialProvider(response.providers)?.practitionerId;
      });
    } catch {
      setOptionsError("We couldn't load our providers and clinic locations. Please try again.");
    } finally {
      setOptionsLoading(false);
    }
  }, [tenant, findInitialLocation, findInitialProvider]);
  const selectedProvider = useMemo(() => selectedProviderId ? providers.find(provider_1 => provider_1.practitionerId === selectedProviderId) ?? null : null, [providers, selectedProviderId]);
  useEffect(() => {
    if (selectedProviderId && !selectedProvider) {
      setSelectedProviderId(undefined);
    }
  }, [selectedProvider, selectedProviderId]);
  const availabilityScopeKey = schedulingMode === "location" ? selectedLocation ? `location:${selectedLocation.locationId}` : null : selectedProviderId ? `provider:${selectedProviderId}` : null;

  // Reset availability whenever the active search scope or visit type changes so stale slots can
  // never win a race. The inactive mode's selection deliberately does not invalidate the search.
  useEffect(() => {
    availabilityRequestId.current += 1;
    setAvailability(null);
    setAvailabilityLoading(false);
    setIsLoadingMore(false);
    setAvailabilityError(null);
    setLoadedBaseKey(null);
    setSelectedSlot(null);
    setWindowDays(INITIAL_WINDOW_DAYS);
  }, [availabilityScopeKey, isReturningPatient]);
  const loadAvailability = useCallback(async (days: number, opts?: {
    more?: boolean;
  }) => {
    if (!availabilityScopeKey) {
      return;
    }
    const baseKey = `${availabilityScopeKey}:${appointmentType}`;
    const requestId = ++availabilityRequestId.current;
    if (opts?.more) {
      setIsLoadingMore(true);
    } else {
      setAvailabilityLoading(true);
      // Mark this (location, visit-type) as attempted up front so a failed fetch doesn't
      // re-trigger the effect in a loop; the error state offers an explicit "Try again".
      setLoadedBaseKey(baseKey);
    }
    setAvailabilityError(null);
    try {
      // Anchor the window on the clinic's "today" (not the visitor's browser day) so the server's
      // timezone-aware past-date check agrees with what we request.
      const fromDate = getCurrentIsoDateInTimezone(clinicTimezone);
      const response_0 = await fetchOnlineSchedulingAvailability({
        location: schedulingMode === "location" ? selectedLocation?.locationId : null,
        appointmentType,
        fromDate,
        toDate: dateKeyFromDate(addDays(dateFromKey(fromDate), days)),
        practitionerIds: schedulingMode === "provider" && selectedProviderId ? [selectedProviderId] : undefined
      }, tenant);
      if (requestId !== availabilityRequestId.current) {
        return;
      }
      setAvailability(response_0);
      setWindowDays(days);
      setLoadedBaseKey(baseKey);
    } catch {
      if (requestId === availabilityRequestId.current) {
        setAvailabilityError("We couldn't load appointment times. Please refresh the page or call the clinic to book.");
      }
    } finally {
      if (requestId === availabilityRequestId.current) {
        if (opts?.more) {
          setIsLoadingMore(false);
        } else {
          setAvailabilityLoading(false);
        }
      }
    }
  }, [availabilityScopeKey, appointmentType, schedulingMode, selectedLocation, selectedProviderId, tenant, clinicTimezone]);

  // Fetch availability when the patient reaches the time step (or changes the active search scope / visit type).
  useEffect(() => {
    if (step !== "time" || !availabilityScopeKey) {
      return;
    }
    const baseKey_0 = `${availabilityScopeKey}:${appointmentType}`;
    if (loadedBaseKey === baseKey_0 || availabilityLoading) {
      return;
    }
    void loadAvailability(INITIAL_WINDOW_DAYS);
  }, [step, availabilityScopeKey, appointmentType, loadedBaseKey, availabilityLoading, loadAvailability]);
  const locationProviderOptions = useMemo(() => {
    const availableProviderIds = new Set((availability?.slots ?? []).flatMap(slot => slot.practitionerId ? [slot.practitionerId] : []));
    return providers.filter(provider_2 => availableProviderIds.has(provider_2.practitionerId));
  }, [availability, providers]);
  useEffect(() => {
    if (locationProviderFilterId && !locationProviderOptions.some(provider_3 => provider_3.practitionerId === locationProviderFilterId)) {
      setLocationProviderFilterId(null);
      setSelectedSlot(null);
    }
  }, [locationProviderFilterId, locationProviderOptions]);
  const visibleSlots = useMemo(() => {
    const slots = availability?.slots ?? [];
    if (schedulingMode !== "location" || !locationProviderFilterId) {
      return slots;
    }
    return slots.filter(slot_0 => slot_0.practitionerId === locationProviderFilterId);
  }, [availability, locationProviderFilterId, schedulingMode]);
  const locationDisplayNameById = useMemo(() => new Map(locations.map(location_1 => [location_1.locationId, location_1.displayName])), [locations]);
  const normalizedSlots = useMemo<NormalizedSlot[]>(() => {
    return visibleSlots.map(slot_1 => ({
      ...normalizeAvailabilitySlot(slot_1, (slot_1.locationId ? locationDisplayNameById.get(slot_1.locationId) : undefined) ?? slot_1.locationName),
      id: availabilitySlotKey(slot_1)
    }));
  }, [locationDisplayNameById, visibleSlots]);
  const visibleSlotByKey = useMemo(() => new Map(visibleSlots.map(slot_2 => [availabilitySlotKey(slot_2), slot_2])), [visibleSlots]);
  const selectedAppointmentLocation = useMemo(() => {
    if (!selectedSlot) {
      return schedulingMode === "location" ? selectedLocation : null;
    }
    const slotLocation = selectedSlot.locationId ? locations.find(location_2 => location_2.locationId === selectedSlot.locationId) : undefined;
    return slotLocation ?? (schedulingMode === "location" ? selectedLocation : null);
  }, [locations, schedulingMode, selectedLocation, selectedSlot]);
  const currentIndex = step === "done" ? -1 : SCHEDULING_STEPS.indexOf(step);
  const canContinue = step === "visit" ? schedulingMode === "location" ? selectedLocation !== null : selectedProvider !== null : step !== "time" || selectedSlot !== null && selectedAppointmentLocation !== null;
  const goNext = () => {
    if (currentIndex >= 0 && currentIndex < SCHEDULING_STEPS.length - 1) {
      setStep(SCHEDULING_STEPS[currentIndex + 1]);
    }
  };
  const goBack = () => {
    if (currentIndex > 0) {
      setStep(SCHEDULING_STEPS[currentIndex - 1]);
    }
  };
  const handleDetailsSubmit = (values: PatientDetailsDraft) => {
    setDetails(values);
    setStep("review");
  };

  // Until the first fetch for the current scope resolves, show the picker's loading state rather
  // than a one-frame flash of the "no appointments" empty state on step entry.
  const availabilityBaseKey = availabilityScopeKey ? `${availabilityScopeKey}:${appointmentType}` : null;
  const availabilityPending = step === "time" && availabilityBaseKey !== null && loadedBaseKey !== availabilityBaseKey;

  // Stable handler so the picker's calendar effects don't re-run on every render.
  const handleSelectSlot = useCallback((slot_3: NormalizedSlot | null) => {
    setSelectedSlot(slot_3 ? visibleSlotByKey.get(slot_3.id) ?? null : null);
  }, [visibleSlotByKey]);
  const handleLocationProviderFilterChange = useCallback((providerId: string | null) => {
    setLocationProviderFilterId(providerId);
    setSelectedSlot(null);
  }, []);
  const handleLoadMore = useCallback(() => {
    void loadAvailability(Math.min(windowDays + WINDOW_INCREMENT_DAYS, MAX_WINDOW_DAYS), {
      more: true
    });
  }, [loadAvailability, windowDays]);
  const confirmBooking = async (smsConsent: boolean) => {
    if (!selectedAppointmentLocation || !selectedSlot) {
      return;
    }
    if (!selectedSlot.practitionerId) {
      setBookingError("This appointment is missing a provider. Please choose another time.");
      return;
    }
    setIsBooking(true);
    setBookingError(null);
    const request: OnlineSchedulingBookRequest = {
      location: selectedAppointmentLocation.locationId,
      firstName: details.firstName.trim(),
      lastName: details.lastName.trim(),
      dateOfBirth: details.dateOfBirth,
      phone: details.phone.trim(),
      // Validated to a PersonGender value by the details-step schema before we reach here.
      gender: details.gender as PersonGender || undefined,
      email: details.email.trim() || undefined,
      homeAddress: {
        line1: details.homeAddress.line1.trim(),
        line2: details.homeAddress.line2.trim() || null,
        city: details.homeAddress.city.trim(),
        state: details.homeAddress.state as USState,
        postalCode: details.homeAddress.postalCode.trim(),
        country: "USA",
        placeId: details.homeAddress.placeId
      },
      reasonForVisit: sanitizeReasonForVisit(reasonForVisit) || undefined,
      isReturningPatient,
      smsConsent,
      insurance: details.insurancePayer.trim() || details.insuranceMemberId.trim() ? {
        payer: details.insurancePayer.trim() || undefined,
        memberId: details.insuranceMemberId.trim() || undefined
      } : undefined,
      guardian: isMinorDateOfBirth(details.dateOfBirth) && (details.guardianFirstName.trim() || details.guardianLastName.trim() || details.guardianPhone.trim()) ? {
        firstName: details.guardianFirstName.trim() || undefined,
        lastName: details.guardianLastName.trim() || undefined,
        phone: details.guardianPhone.trim() || undefined
      } : undefined,
      appointmentSlot: {
        appointmentId: selectedSlot.appointmentId,
        appointmentType: selectedSlot.appointmentType as AvailabilityAppointmentType,
        ehrAppointmentTypeId: selectedSlot.ehrAppointmentTypeId,
        start: selectedSlot.start,
        end: selectedSlot.end,
        practitionerId: selectedSlot.practitionerId,
        locationId: selectedAppointmentLocation.locationId
      }
    };
    try {
      const result = await bookOnlineSchedulingAppointment(request, tenant);
      setBookingResult(result);
      setStep("done");
    } catch {
      setBookingError("Please choose another time, or call the clinic to book your visit.");
    } finally {
      setIsBooking(false);
    }
  };
  const resetFlow = () => {
    setBookingResult(null);
    setBookingError(null);
    setSelectedSlot(null);
    setDetails(EMPTY_PATIENT_DETAILS);
    setReasonForVisit("");
    setIsReturningPatient(false);
    const initialLocation = findInitialLocation(locations);
    const initialProvider = findInitialProvider(providers);
    setSelectedLocation(initialLocation);
    setSelectedProviderId(initialProvider?.practitionerId);
    setLocationProviderFilterId(null);
    setSchedulingMode(initialProviderId ? "provider" : "location");
    setStep("visit");
  };

  // Success screen.
  if (step === "done" && bookingResult && selectedAppointmentLocation && selectedSlot) {
    return <Card className="rounded-2xl shadow-sm">
        <CardContent className="pt-6">
          <SuccessStep location={selectedAppointmentLocation} slotStart={selectedSlot.start} providerDisplayName={bookingResult.confirmedAppointment.providerDisplayName} patientName={details.firstName.trim() || "there"} onBookAnother={resetFlow} />
        </CardContent>
      </Card>;
  }
  const optionsEmpty = locations.length === 0 || providers.length === 0;
  if (optionsLoading || optionsError || optionsEmpty) {
    return <Card className="rounded-2xl shadow-sm">
        <CardContent className="pt-6">
          {optionsLoading ? <div className="space-y-4 py-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div> : <div className="flex flex-col items-center gap-4 py-2">
              <ErrorAlert compact message={optionsError ?? "Online scheduling isn't available right now. Please call the clinic to book your visit."} />
              <Button variant="outline" onClick={() => void loadOptions()}>
                Try again
              </Button>
            </div>}
        </CardContent>
      </Card>;
  }
  const title = step !== "done" ? STEP_TITLE[step] : null;
  return <div className="space-y-6">
      <ProgressSteps current={step === "done" ? "review" : step} />

      <Card className="rounded-2xl shadow-sm">
        {title && <CardHeader className={cn("pb-4", step === "visit" && "gap-3 space-y-0 sm:flex-row sm:items-center sm:justify-between")}>
            <H4 className="text-foreground">{title}</H4>
            {step === "visit" && <SchedulingModeToggle value={schedulingMode} onChange={mode => {
          setSchedulingMode(mode);
          setLocationProviderFilterId(null);
        }} />}
          </CardHeader>}

        <CardContent className="space-y-6">
          {step === "visit" && <div className="space-y-6">
              {schedulingMode === "location" ? <LocationSelectionField locations={locations} selected={selectedLocation} onChange={setSelectedLocation} /> : <ProviderSelectionField providers={providers} selectedProviderId={selectedProviderId} onChange={providerId_0 => setSelectedProviderId(providerId_0 ?? undefined)} />}
              <div className="border-t pt-6">
                <VisitStep isReturningPatient={isReturningPatient} onChangeReturning={setIsReturningPatient} reasonForVisit={reasonForVisit} onChangeReason={setReasonForVisit} brandName={brandName} />
              </div>
            </div>}

          {step === "time" && availabilityScopeKey && <div className="space-y-5">
              {schedulingMode === "location" && selectedLocation && <div className="space-y-3">
                  <LocationSelectionField locations={locations} selected={selectedLocation} onChange={setSelectedLocation} readOnly />
                  {locationProviderOptions.length > 1 && <ProviderSelectionField providers={locationProviderOptions} selectedProviderId={locationProviderFilterId} onChange={handleLocationProviderFilterChange} allowAny />}
                </div>}
              {schedulingMode === "provider" && selectedProvider && <ProviderSelectionField providers={providers} selectedProviderId={selectedProvider.practitionerId} onChange={providerId_1 => setSelectedProviderId(providerId_1 ?? undefined)} readOnly />}

              <AppointmentSlotPicker slots={normalizedSlots} isLoading={availabilityLoading || availabilityPending} error={availabilityError} selectedSlotId={selectedSlot ? availabilitySlotKey(selectedSlot) : null} onSelectSlot={handleSelectSlot} onLoadMore={handleLoadMore} isLoadingMore={isLoadingMore} canLoadMore={windowDays < MAX_WINDOW_DAYS} windowDays={windowDays} maxWindowDays={MAX_WINDOW_DAYS} slotSecondaryLabel={schedulingMode === "provider" ? "location" : "provider"} showProviderSummary={schedulingMode === "location" && locationProviderOptions.length <= 1} emptyHint={schedulingMode === "provider" ? "Try searching further out, or go back to choose a different provider." : undefined} />
            </div>}

          {step === "details" && <DetailsStep formId={DETAILS_FORM_ID} defaultValues={details} onSubmit={handleDetailsSubmit} />}

          {step === "review" && selectedAppointmentLocation && selectedSlot && <ReviewStep location={selectedAppointmentLocation} isReturningPatient={isReturningPatient} reasonForVisit={reasonForVisit} slot={selectedSlot} details={details} onBack={goBack} onConfirm={confirmBooking} isBooking={isBooking} bookingError={bookingError} />}

          {step !== "done" && step !== "review" && <div className="border-t pt-4">
              <StepActions primaryLabel="Continue" primaryType={step === "details" ? "submit" : "button"} form={step === "details" ? DETAILS_FORM_ID : undefined} onPrimary={step === "details" ? undefined : goNext} primaryDisabled={!canContinue} onBack={currentIndex > 0 ? goBack : undefined} />
            </div>}
        </CardContent>
      </Card>
    </div>;
}
function SchedulingModeToggle(t0) {
  const $ = _c(6);
  const {
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = mode => {
      if (mode === "location" || mode === "provider") {
        onChange(mode);
      }
    };
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <TabsList aria-label="Choose how to find an appointment"><TabsTrigger value="location">By location</TabsTrigger><TabsTrigger value="provider">By provider</TabsTrigger></TabsList>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== value) {
    t3 = <Tabs value={value} onValueChange={t1}>{t2}</Tabs>;
    $[3] = t1;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}